import React, { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Grid, Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
import { fetchOptions } from '../queries';
import useIsMobile from '../../../../hooks/useIsMobile';
import Select from './Select';

const SelectLimitedSelection = ({ name, query, addNullOption, props, user, findRef, highlight, disableIfDefault, isEdit }) => {
    const isLoaded = useRef(false);
    const [options, setOptions] = useState([]);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const isMobile = useIsMobile();
    const {
        clearErrors,
        formState: { errors, defaultValues },
        selectedValues,
        setError,
        setValue,
        getValues,
        watch,
        // wrap
    } = useFormContext();
    const { endpoint, where = {}, fields, order, labelKey, roleConstraints, populate, useLoopback } = query;
    const { referenceField, referenceKey, findKey } = findRef || {};
    const { field: highlightField } = highlight || {};

    const referenceList = !!referenceField && selectedValues[referenceField]?.[referenceKey];

    useEffect(() => {
        const getOptions = async () => {
            const optionsResponse = await fetchOptions({ endpoint, where, fields, order, labelKey, roleConstraints, user, populate, useLoopback });
            if (optionsResponse.error) {
                setOptions([]);
                setError(name, { type: 'fetch options', message: `Could not fetch ${props.label} options` });
            } else {
                if (addNullOption) optionsResponse.unshift(addNullOption);
                setOptions(optionsResponse);
                clearErrors([name]);
            }
        };

        getOptions();
    }, []);

    useEffect(() => {
        if (referenceList && referenceList.length && options?.length && !disableIfDefault && !defaultValues[name]) {
            const highlightId = getValues(highlightField);

            // Filter options based on values from reference
            const referenceOptions = showAll ? options : options.filter((opt) => referenceList.includes(opt[findKey]));

            // Ensure highlight option is included in reference list
            if (highlightId && !referenceOptions.some((opt) => opt._id === highlightId)) {
                const highlightedOption = options.find((opt) => opt._id === highlightId);
                if (highlightedOption) {
                    referenceOptions.push(highlightedOption);
                }
            }

            const MAX_SORT_INDEX = 999999;
            const referenceOrder = new Map(referenceList.map((id, i) => [id, i]));
            referenceOptions.sort((a, b) => {
                const indexA = referenceOrder.get(a[findKey]) ?? MAX_SORT_INDEX;
                const indexB = referenceOrder.get(b[findKey]) ?? MAX_SORT_INDEX;
                return indexA - indexB;
            });

            // Ensure highlight option is pinned to top of list
            if (highlightId) {
                const highlightIndex = referenceOptions.findIndex((opt) => opt._id === highlightId);
                if (highlightIndex >= 0) referenceOptions.unshift(referenceOptions.splice(highlightIndex, 1)[0]);
            }

            if (addNullOption) referenceOptions.push({ ...addNullOption });

            const defaultValue = defaultValues[name];

            // If value option is not in referenceOptions, add it to referenceOptions
            const existingReferenceOption = referenceOptions.find((opt) => opt[findKey] === defaultValue);
            if (defaultValue !== undefined && !existingReferenceOption) {
                const existingOption = options.find((opt) => opt[findKey] === defaultValue);
                if (existingOption) {
                    referenceOptions.push(existingOption);
                }
            }

            // if is creating or changing reference, auto-set value based on incoming reference
            if (!isEdit || isLoaded.current) {
                // Only over write value after initial values have run
                const found = referenceOptions.find((opt) => referenceList.includes(opt[findKey] ?? null));
                if (found) setValue(name, found._id);
            }
            isLoaded.current = true;
            setFilteredOptions(referenceOptions);
        } else {
            setFilteredOptions(options);
        }
    }, [referenceList, options, showAll]);

    const handleCheckboxChange = (val) => {
        const check = val.target.checked;
        if (check) {
            setFilteredOptions(options.filter((opt) => referenceList?.includes(opt[findKey])));
        } else {
            setFilteredOptions(options);
        }
        setShowAll(check);
    };

    if (!filteredOptions.length) return <CircularProgress />;
    return (
        <Grid container sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Grid xs={isMobile ? 12 : 8} sx={{ mr: 1 }}>
                <Select
                    name={name}
                    labelKey={labelKey}
                    key={filteredOptions}
                    props={{ ...props, options: filteredOptions, highlight }}
                    loading={!options && !errors[name]}
                    addNullOption={addNullOption}
                    // wrap
                />
            </Grid>
            <Grid xs={4} sx={{ ml: 1 }}>
                <FormControlLabel
                    label="Show all?"
                    slotProps={{ typography: { noWrap: true, variant: 'caption' } }}
                    control={
                        <Checkbox
                            checked={showAll}
                            onChange={handleCheckboxChange}
                            size="small"
                            // wrap
                        />
                    }
                    // wrap
                />
            </Grid>
        </Grid>
    );
};

export default SelectLimitedSelection;
